import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ExternalLink, Loader2, Link2Off, RefreshCw, Check, X, Pencil, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type Status = "proposed" | "confirmed" | "synthesising" | "synthesised" | "failed" | "rejected";

const STATUS_STYLES: Record<Status, string> = {
  proposed: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  confirmed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  synthesising: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  synthesised: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  rejected: "bg-muted text-muted-foreground",
};

function fmt(d?: string | null) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
}

interface Capsule {
  id: string;
  title: string;
  organisation: string | null;
  meeting_at: string | null;
  status: Status;
  auto_merged: boolean;
  confidence: number | null;
  consolidated_summary: string | null;
  consolidated_extractions: any;
  multimodal_synthesis_run_id: string | null;
  synthesis_error: string | null;
  synthesised_at: string | null;
  participants: string[] | null;
}

interface SourceLink {
  capsule_id: string;
  source_id: string;
  role: string;
  source: {
    id: string;
    title: string | null;
    source_type: string;
    external_url: string | null;
    occurred_at: string | null;
    word_count: number | null;
    created_at: string;
  } | null;
}

export default function ResearchCapsuleDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const author = useResearchCapability("author");
  const manage = useResearchCapability("manage");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [resynthBusy, setResynthBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const capsuleKey = ["research_capsule", id];
  const sourcesKey = ["research_capsule_sources", id];
  const runKey = ["research_capsule_run", id];

  const { data: capsule } = useQuery({
    queryKey: capsuleKey,
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_meeting_capsules")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data as Capsule | null;
    },
  });

  const { data: sources } = useQuery({
    queryKey: sourcesKey,
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_meeting_capsule_sources")
        .select("capsule_id, source_id, role, source:research_sources(id, title, source_type, external_url, occurred_at, word_count, created_at)")
        .eq("capsule_id", id!);
      if (error) throw error;
      return (data as any[]) as SourceLink[];
    },
  });

  const { data: run } = useQuery({
    queryKey: runKey,
    enabled: !!capsule?.multimodal_synthesis_run_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_ai_call_runs")
        .select("model, input_tokens, output_tokens, cost_usd, latency_ms, cache_hit, multi_agent_applied")
        .eq("id", capsule!.multimodal_synthesis_run_id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Realtime + poll while in-flight
  useEffect(() => {
    if (!id) return;
    const ch = supabase
      .channel(`research_capsule_${id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "research_meeting_capsules", filter: `id=eq.${id}` },
        () => qc.invalidateQueries({ queryKey: capsuleKey })
      )
      .subscribe();
    let timer: any;
    if (capsule && (capsule.status === "confirmed" || capsule.status === "synthesising")) {
      timer = setInterval(() => qc.invalidateQueries({ queryKey: capsuleKey }), 5000);
    }
    return () => {
      supabase.removeChannel(ch);
      if (timer) clearInterval(timer);
    };
  }, [id, capsule?.status, qc]);

  if (!capsule) {
    return (
      <div className="space-y-4">
        <Link to="/partner/research/capsules" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to capsules
        </Link>
        <Card><CardContent className="p-10 text-center text-muted-foreground">Loading…</CardContent></Card>
      </div>
    );
  }

  const status = capsule.status;
  const ext = capsule.consolidated_extractions;
  // role='evidence' capsule sources — attached via the suggestions-inbox thesis_attachment
  // "capsule" target (research_meeting_capsule_sources.role, §4.3), surfaced distinctly from
  // the capsule's regular constituent sources.
  const evidenceSources = (sources ?? []).filter((s) => s.role === "evidence");

  const onConfirm = async () => {
    const { error } = await supabase
      .from("research_meeting_capsules")
      .update({ status: "confirmed", human_confirmed_by: user?.id, human_confirmed_at: new Date().toISOString() })
      .eq("id", capsule.id);
    if (error) return toast({ title: "Confirm failed", description: error.message, variant: "destructive" });
    toast({ title: "Confirmed", description: "Synthesis usually completes in 30–60 seconds." });
    qc.invalidateQueries({ queryKey: capsuleKey });
  };

  const onReject = async () => {
    const { error } = await supabase
      .from("research_meeting_capsules")
      .update({ status: "rejected" })
      .eq("id", capsule.id);
    if (error) return toast({ title: "Reject failed", description: error.message, variant: "destructive" });
    toast({ title: "Rejected" });
    qc.invalidateQueries({ queryKey: capsuleKey });
  };

  const onResynth = async () => {
    setResynthBusy(true);
    const { error } = await supabase.functions.invoke("research-capsule-synthesise", {
      body: { capsule_id: capsule.id },
    });
    setResynthBusy(false);
    if (error) return toast({ title: "Re-synthesis failed", description: error.message, variant: "destructive" });
    toast({ title: "Re-synthesised", description: "Refreshed from latest sources." });
    qc.invalidateQueries({ queryKey: capsuleKey });
    qc.invalidateQueries({ queryKey: runKey });
  };

  const onUnlink = async (sourceId: string) => {
    const { error } = await supabase
      .from("research_meeting_capsule_sources")
      .delete()
      .eq("capsule_id", capsule.id)
      .eq("source_id", sourceId);
    if (error) return toast({ title: "Unlink failed", description: error.message, variant: "destructive" });
    toast({ title: "Source unlinked" });
    qc.invalidateQueries({ queryKey: sourcesKey });
  };

  const saveTitle = async () => {
    const t = titleDraft.trim();
    if (!t || t === capsule.title) { setEditingTitle(false); return; }
    const { error } = await supabase.from("research_meeting_capsules").update({ title: t }).eq("id", capsule.id);
    if (error) return toast({ title: "Rename failed", description: error.message, variant: "destructive" });
    setEditingTitle(false);
    qc.invalidateQueries({ queryKey: capsuleKey });
  };

  return (
    <div className="space-y-6">
      <Link to="/partner/research/capsules" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to capsules
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          {editingTitle ? (
            <div className="flex gap-2">
              <Input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} className="text-lg font-semibold" autoFocus />
              <Button size="sm" onClick={saveTitle}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">{capsule.title}</h2>
              {author.data && (
                <Button variant="ghost" size="icon" onClick={() => { setTitleDraft(capsule.title); setEditingTitle(true); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className={cn("border-0", STATUS_STYLES[status])}>
              {status === "synthesising" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} {status}
            </Badge>
            <span>{capsule.organisation ?? "—"}</span>
            <span>•</span>
            <span>{fmt(capsule.meeting_at)}</span>
            <span>•</span>
            <span>Confidence {Number(capsule.confidence ?? 0).toFixed(2)}</span>
            {capsule.auto_merged && <Badge variant="secondary">Auto-merged</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {status === "proposed" && author.data && (
            <>
              <Button size="sm" onClick={onConfirm}><Check className="h-4 w-4 mr-1" /> Confirm</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline"><X className="h-4 w-4 mr-1" /> Reject</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reject this capsule?</AlertDialogTitle>
                    <AlertDialogDescription>Sources remain in the corpus; only the capsule is rejected.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onReject}>Reject</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {(status === "synthesised" || status === "failed") && manage.data && (
            <Button size="sm" variant="outline" disabled={resynthBusy} onClick={onResynth}>
              {resynthBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Re-synthesise
            </Button>
          )}
        </div>
      </div>

      {status === "failed" && (
        <Card className="border-red-500/50 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4 space-y-1">
            <p className="font-medium text-red-800 dark:text-red-200">Synthesis failed</p>
            <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">{capsule.synthesis_error ?? "Unknown error."}</p>
            <p className="text-xs text-muted-foreground">Check that each source has non-empty extracted text, then Re-synthesise.</p>
          </CardContent>
        </Card>
      )}

      {/* Sources timeline */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">Sources ({sources?.length ?? 0})</CardTitle></CardHeader>
        <CardContent>
          {!sources || sources.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sources linked.</p>
          ) : (
            <ol className="relative border-l ml-2 space-y-4">
              {[...sources]
                .sort((a, b) => new Date(a.source?.occurred_at ?? a.source?.created_at ?? 0).getTime() - new Date(b.source?.occurred_at ?? b.source?.created_at ?? 0).getTime())
                .map((s) => (
                  <li key={s.source_id} id={`source-${s.source_id}`} className="ml-4">
                    <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-primary" />
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{s.role}</Badge>
                      <span className="text-xs text-muted-foreground">{s.source?.source_type}</span>
                      {s.source?.external_url ? (
                        <a href={s.source.external_url} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                          {s.source?.title ?? "(untitled source)"} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="font-medium">{s.source?.title ?? "(untitled source)"}</span>
                      )}
                      {manage.data && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm"><Link2Off className="h-3.5 w-3.5 mr-1" /> Unlink</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Unlink this source?</AlertDialogTitle>
                              <AlertDialogDescription>The source stays in the corpus but is removed from this capsule. Re-synthesise afterwards.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onUnlink(s.source_id)}>Unlink</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {fmt(s.source?.occurred_at ?? s.source?.created_at)}
                      {s.source?.word_count != null && <> · {s.source.word_count.toLocaleString()} words</>}
                    </div>
                  </li>
                ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Evidence sources — attached via the suggestions-inbox thesis_attachment "capsule" target
          (role='evidence'), distinct from the capsule's constituent sources above. */}
      {evidenceSources.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-lg">Evidence ({evidenceSources.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {evidenceSources.map((s) => (
                <div key={s.source_id} className="text-sm border border-border rounded p-2 flex flex-wrap items-center gap-2">
                  <Badge variant="outline">evidence</Badge>
                  <span className="text-xs text-muted-foreground">{s.source?.source_type}</span>
                  {s.source?.external_url ? (
                    <a href={s.source.external_url} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                      {s.source?.title ?? "(untitled source)"} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-medium">{s.source?.title ?? "(untitled source)"}</span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto">{fmt(s.source?.occurred_at ?? s.source?.created_at)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Synthesis result */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Synthesis</CardTitle>
        </CardHeader>
        <CardContent>
          {!capsule.consolidated_summary && status === "synthesised" ? (
            <p className="text-sm text-muted-foreground">Synthesis produced no output. Try Re-synthesise.</p>
          ) : !capsule.consolidated_summary ? (
            <p className="text-sm text-muted-foreground">No synthesis yet. Confirm the capsule to queue synthesis.</p>
          ) : (
            <Tabs defaultValue={ext ? "structured" : "raw"}>
              <TabsList>
                <TabsTrigger value="structured" disabled={!ext}>Structured</TabsTrigger>
                <TabsTrigger value="raw">Raw</TabsTrigger>
              </TabsList>
              <TabsContent value="structured" className="space-y-6 pt-4">
                {!ext ? (
                  <p className="text-sm text-muted-foreground">Structured extraction unavailable — see Raw tab.</p>
                ) : (
                  <StructuredSynthesis ext={ext} />
                )}
              </TabsContent>
              <TabsContent value="raw" className="pt-4">
                <pre className="text-xs whitespace-pre-wrap bg-muted/30 p-4 rounded border max-h-[600px] overflow-auto">
                  {capsule.consolidated_summary}
                </pre>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* Synthesis details — model/tokens/cost/latency/cache-hit, collapsed by default so
          the synthesis above leads and the diagnostics stay one click away. */}
      {run && (
        <Collapsible className="border border-border rounded-lg bg-muted/30">
          <CollapsibleTrigger className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-muted/50 rounded-lg">
            <span className="text-sm font-semibold">Synthesis details</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform [&[data-state=open]]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat label="Model" value={(run as any).model ?? "—"} />
              <Stat label="Input tokens" value={(run as any).input_tokens?.toLocaleString() ?? "—"} />
              <Stat label="Output tokens" value={(run as any).output_tokens?.toLocaleString() ?? "—"} />
              <Stat label="Cost (USD)" value={(run as any).cost_usd != null ? `$${Number((run as any).cost_usd).toFixed(4)}` : "—"} />
              <Stat label="Latency" value={(run as any).latency_ms != null ? `${(run as any).latency_ms} ms` : "—"} />
              <Stat label="Cache hit" value={(run as any).cache_hit ? "Yes" : "No"} />
              <Stat label="Multi-agent" value={(run as any).multi_agent_applied ? "Yes" : "No"} />
              <Stat label="Synthesised at" value={fmt(capsule.synthesised_at)} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function scrollToSource(sourceId?: string) {
  if (!sourceId) return;
  const el = document.getElementById(`source-${sourceId}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function StructuredSynthesis({ ext }: { ext: any }) {
  const keyClaims: any[] = ext.key_claims ?? [];
  const macroPreds: any[] = ext.macro_predictions ?? [];
  const risks: any[] = ext.risks ?? [];
  const actions: any[] = ext.action_items ?? [];
  const attendees: string[] = ext.attendees ?? [];
  const confidence = ext.confidence;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold mb-1">Executive summary</h3>
          <p className="text-sm whitespace-pre-wrap">{ext.exec_summary ?? "—"}</p>
        </div>
        {confidence != null && (
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Confidence</div>
            <div className="font-mono">{Number(confidence).toFixed(2)}</div>
          </div>
        )}
      </div>

      <Section title="Key claims">
        {keyClaims.length === 0 ? <Empty /> : (
          <SimpleTable
            head={["Claim", "Source", "Chunk/Slide"]}
            rows={keyClaims.map((c) => [
              <span>{c.claim ?? c.text}</span>,
              <SourceLinkCell sourceId={c.source_id} label={c.source ?? c.source_id} />,
              <span className="text-xs text-muted-foreground">{c.chunk_index ?? c.slide_index ?? "—"}</span>,
            ])}
          />
        )}
      </Section>

      <Section title="Macro predictions">
        {macroPreds.length === 0 ? <Empty /> : (
          <SimpleTable
            head={["Prediction", "Horizon", "Conviction", "Source"]}
            rows={macroPreds.map((m) => [
              <span>{m.prediction ?? m.text}</span>,
              <span>{m.horizon ?? "—"}</span>,
              <span>{m.conviction ?? "—"}</span>,
              <SourceLinkCell sourceId={m.source_id} label={m.source ?? m.source_id} />,
            ])}
          />
        )}
      </Section>

      <Section title="Risks">
        {risks.length === 0 ? <Empty /> : (
          <SimpleTable
            head={["Risk", "Mitigant", "Source"]}
            rows={risks.map((r) => [
              <span>{r.risk ?? r.text}</span>,
              <span>{r.mitigant ?? "—"}</span>,
              <SourceLinkCell sourceId={r.source_id} label={r.source ?? r.source_id} />,
            ])}
          />
        )}
      </Section>

      <Section title="Action items">
        {actions.length === 0 ? <Empty /> : (
          <SimpleTable
            head={["Action", "Due", "Owner", "Source", ""]}
            rows={actions.map((a) => [
              <span>{a.text ?? a.action}</span>,
              <span>{a.due ?? "—"}</span>,
              <span>{a.owner ?? "—"}</span>,
              <SourceLinkCell sourceId={a.source_id} label={a.source ?? a.source_id} />,
              <Button size="sm" variant="outline" disabled title="Coming in Phase 5">Create Asana task</Button>,
            ])}
          />
        )}
      </Section>

      <Section title="Attendees">
        {attendees.length === 0 ? <Empty /> : (
          <div className="flex flex-wrap gap-2">
            {attendees.map((a: any, i) => {
              const label = typeof a === "string" ? a : (a?.name ?? a?.role ?? "—");
              const note = typeof a === "object" ? a?.note : null;
              return (
                <Badge key={i} variant="secondary" title={note ?? undefined}>
                  {label}{typeof a === "object" && a?.confirmed === false ? " (tentative)" : ""}
                </Badge>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}
function Empty() {
  return <p className="text-sm text-muted-foreground">No entries.</p>;
}
function SimpleTable({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="overflow-x-auto border rounded">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>{head.map((h, i) => <th key={i} className="text-left p-2 font-medium text-muted-foreground">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              {r.map((c, j) => <td key={j} className="p-2 align-top">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function SourceLinkCell({ sourceId, label }: { sourceId?: string; label?: any }) {
  if (!sourceId) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <button onClick={() => scrollToSource(sourceId)} className="text-primary hover:underline text-xs font-mono">
      {String(label ?? sourceId).slice(0, 12)}…
    </button>
  );
}
